import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { QUERIES } from '../constants/mediaQueries';
import { BUTTON_SIZE_MULTIPLIERS, BUTTON_TEXT_SIZE_MAP } from '../constants/sizes';

import { ColorConfig, ColorScheme, IconColor } from '../config/colorSchemes';
import { isActivePath } from '../utils/navigation';
import { isExternalLink, withSmoothScroll } from '../utils/linkUtils';
import Icon from './Icon';
import {
  ButtonSize,
  ButtonProps,
  TextSize,
  ButtonIconPosition,
  ButtonVariant,
} from '../types/button';

/**
 * Generates size-specific CSS for buttons based on the provided size
 * Now only handles padding - text size is handled separately
 *
 * @param size - The button size variant to generate styles for
 * @returns CSS styles with appropriate padding
 */
const getSizeStyles = (size: ButtonSize = 'medium') => {
  const factor = BUTTON_SIZE_MULTIPLIERS[size];
  return css`
    padding: calc(var(--button-padding-v) * ${factor}) calc(var(--button-padding-h) * ${factor});
  `;
};

/**
 * Generates text size-specific CSS for buttons based on the provided text size
 * This allows independent control of text size separate from button padding
 *
 * @param textSize - The text size variant to generate styles for
 * @returns CSS styles with appropriate font size
 */
const getTextSizeStyles = (textSize: TextSize = 'medium') => {
  return css`
    font-size: ${BUTTON_TEXT_SIZE_MAP[textSize]};
  `;
};

/**
 * Gets color configuration for a button variant
 * Consolidates all color logic in one place for easier maintenance
 */
const getColorConfig = (
  colorScheme: ColorScheme = 'light',
  isActive = false,
  variant: ButtonVariant = 'primary'
) => {
  const colors = ColorConfig[colorScheme];

  if (variant === 'link') {
    return {
      background: 'transparent',
      border: 'none',
      text: colors.text,
      hoverBackground: 'transparent',
      hoverBorder: 'none',
      hoverText: colors.hoverBackground,
      activeText: colors.activeText,
      shadow: 'none',
      hoverShadow: 'none',
    };
  }

  if (variant === 'secondary') {
    const secondaryColors = ColorConfig.dark;
    return {
      background: secondaryColors.background,
      border: secondaryColors.border,
      text: secondaryColors.text,
      hoverBackground: secondaryColors.hoverBackground,
      hoverBorder: secondaryColors.hoverBorder,
      hoverText: secondaryColors.hoverText,
      activeText: secondaryColors.activeText,
      shadow: 'none',
      hoverShadow: 'none',
    };
  }

  if (variant === 'cta') {
    return {
      background: colors.ctaBackground,
      border: colors.ctaBorder,
      text: colors.ctaText,
      hoverBackground: colors.ctaHoverBackground,
      hoverBorder: colors.ctaHoverBorder,
      hoverText: colors.ctaHoverText,
      activeText: colors.ctaActiveText,
      shadow: colors.ctaShadow,
      hoverShadow: colors.ctaHoverShadow,
    };
  }

  // primary variant
  if (isActive) {
    return {
      background: colors.activeBackground,
      border: colors.activeBorder,
      text: colors.activeText,
      hoverBackground: colors.activeBackground,
      hoverBorder: colors.activeBorder,
      hoverText: colors.activeText,
      activeText: colors.activeText,
      shadow: 'none',
      hoverShadow: 'none',
    };
  }

  return {
    background: colors.background,
    border: colors.border,
    text: colors.text,
    hoverBackground: colors.hoverBackground,
    hoverBorder: colors.hoverBorder,
    hoverText: colors.hoverText,
    activeText: colors.activeText,
    shadow: 'none',
    hoverShadow: 'none',
  };
};

/**
 * Generates color-specific CSS for buttons based on the provided color scheme and active state
 */
const getColorStyles = (
  colorScheme: ColorScheme = 'light',
  isActive = false,
  variant: ButtonVariant = 'primary'
) => {
  const colorConfig = getColorConfig(colorScheme, isActive, variant);

  return css`
    background: ${colorConfig.background};
    border: ${colorConfig.border};
    color: ${colorConfig.text};
    box-shadow: ${colorConfig.shadow || 'none'};

    &:hover {
      background: ${colorConfig.hoverBackground};
      border: ${colorConfig.hoverBorder};
      color: ${colorConfig.hoverText};
      transform: ${variant === 'link' ? 'none' : 'var(--hover-scale-button)'};
      box-shadow: ${colorConfig.hoverShadow || colorConfig.shadow || 'none'};

      & span {
        color: ${colorConfig.hoverText} !important;
        transition: var(--transition-all-ease);
      }
    }
  `;
};

/**
 * Generates variant-specific CSS (link, secondary, primary)
 */
const getVariantStyles = (
  variant: ButtonVariant = 'primary',
  colorScheme: ColorScheme = 'light'
) => {
  if (variant === 'link') {
    return css`
      padding: 0;
      font-weight: var(--font-weight-semibold);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
        text-underline-offset: 0.22em;
      }
    `;
  }

  if (variant === 'cta') {
    return css`
      font-weight: var(--font-weight-semibold);
      letter-spacing: 0.01em;
    `;
  }

  return '';
};

export interface StyledButtonProps {
  $colorScheme?: ColorScheme;
  $size?: ButtonSize;
  $textSize?: TextSize;
  $variant?: ButtonVariant;
  $isActive?: boolean;
  $disableMotion?: boolean;
  $iconColour?: IconColor;
  $iconPosition?: ButtonIconPosition;
  $showExternalLinkArrowIcon?: boolean;
  className?: string;
  disabled?: boolean;
}

// Shared button styles
const buttonStyles = css<StyledButtonProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: var(--button-gap-standard);
  min-height: var(--height-button-default);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  line-height: 150%;
  cursor: pointer;
  transition:
    var(--transition-all-ease),
    box-shadow 0.3s ease;
  border-radius: var(--radius-button);
  border-style: solid;
  width: fit-content;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  white-space: nowrap;

  /* Apply color styles */
  ${(props) => getColorStyles(props.$colorScheme, props.$isActive, props.$variant)}

  /* Apply size-based styles (padding only) */
  ${(props) => getSizeStyles(props.$size)}

  /* Apply text size styles (font-size only) */
  ${(props) => getTextSizeStyles(props.$textSize)}

  /* Apply variant-specific styles */
  ${(props) => getVariantStyles(props.$variant, props.$colorScheme)}

  /* SVG icon styling */
  ${(props: StyledButtonProps) => {
    const colors = ColorConfig[props.$iconColour || props.$colorScheme || 'light'];
    return css`
      svg {
        fill: ${props.$isActive ? colors.activeText : colors.text};
        transition: var(--transition-all-ease);
      }
      &:hover svg {
        fill: ${colors.hoverText};
      }
    `;
  }}

  /* Icon positioning and sizing */
  ${(props: StyledButtonProps) => {
    if (props.$iconPosition === 'icon-only') {
      return css`
        padding: calc(
          var(--button-padding-icon) * ${BUTTON_SIZE_MULTIPLIERS[props.$size || 'medium']}
        );
        aspect-ratio: 1 / 1;
        &:hover {
          transform: var(--hover-scale-button-icon);
        }
      `;
    }
    if (props.$iconPosition === 'icon-leading' || props.$iconPosition === 'icon-trailing') {
      return css`
        gap: var(--button-gap-icon);
      `;
    }
    return '';
  }}

  /* External link arrow icon */
  ${(props) =>
    props.$showExternalLinkArrowIcon &&
    css`
      &[target='_blank']::after {
        content: '↗';
        margin-left: var(--spacing-xsmall);
        font-size: var(--body-small-size);
        display: inline-block;
        transition: var(--transition-all-ease);
      }
      &[target='_blank']:hover::after {
        transform: var(--hover-translate-y);
      }
    `}

  /* Disabled state */
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    pointer-events: none;
    box-shadow: none;
  }

  /* Button Responsive Styles */
  ${QUERIES.maxMobile} {
    font-size: var(--body-base-size);
  }
`;

/**
 * Styled Component Variants
 * Each component applies the shared buttonStyles to a different HTML element
 * This allows consistent styling across various functional button types
 */

// Regular button element for form submissions and click actions
const StyledButton = styled(motion.button)<StyledButtonProps>`
  ${buttonStyles}
`;

// Anchor tag for both hash links and external links
const StyledAnchorTag = styled(motion.a)<StyledButtonProps>`
  ${buttonStyles}
`;

// Gatsby Link component for internal page navigation with client-side routing
const StyledGatsbyLink = styled(motion(GatsbyLink))<StyledButtonProps>`
  ${buttonStyles}
`;

/**
 * ButtonWrapper - Reusable container for button groups
 * Provides consistent grid layout and responsive behavior for multiple buttons
 */
export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-large);
  width: 100%;
  margin-top: var(--spacing-xlarge);

  ${QUERIES.maxTablet} {
    flex-direction: column;
    align-items: center;
  }
`;

/**
 * Determines the link type based on href
 */
const getLinkType = (href?: string): 'external' | 'hash' | 'internal' | null => {
  if (!href) return null;
  if (href.includes('#')) return 'hash';
  if (isExternalLink(href)) return 'external';
  return 'internal';
};

/**
 * Component type map for link types and button types
 */
const componentMap = {
  default: StyledButton,
  external: StyledAnchorTag,
  hash: StyledAnchorTag,
  internal: StyledGatsbyLink,
};
type ComponentMapKey = keyof typeof componentMap;

/**
 * Gets motion configuration based on disabled and disableMotion flags
 */
const getMotionConfig = (disabled: boolean, disableMotion: boolean) => ({
  whileHover: !disabled && !disableMotion ? { scale: 1.03 } : {},
  whileTap: !disabled && !disableMotion ? { scale: 0.97 } : {},
});

const Button: React.FC<ButtonProps> = ({
  // Content props
  text,
  children,

  // Style props
  colorScheme = 'light',
  size = 'medium',
  textSize = 'medium',
  variant = 'primary',
  disabled = false,
  disableMotion = false,
  className = '',

  // Icon props
  iconName,
  iconSize = 'medium',
  iconColour,
  iconPosition = null,
  showExternalLinkArrowIcon = true,

  // Button behavior props
  type,
  onClick,

  // Link behavior props
  href,
  target,
  rel,

  // Accessibility and identification
  id,
  role,
  ariaLabel,
  isActiveOverride = false,
}) => {
  /**
   * Renders the appropriate content for the button based on icon placement
   * Handles various icon positions (leading, trailing, icon-only) and text content
   *
   * @returns React node with properly formatted content and icons
   */
  const renderContent = () => {
    if (!iconName || !iconPosition) {
      return text || children;
    }

    const icon = (
      <Icon name={iconName} alt={`Icon: ${iconName}`} colour={iconColour} size={iconSize} />
    );

    if (iconPosition === 'icon-only') {
      return icon;
    }

    return (
      <>
        {iconPosition === 'icon-leading' && icon}
        {text || children}
        {iconPosition === 'icon-trailing' && icon}
      </>
    );
  };

  /**
   * Common styling props shared across all button variants
   */
  const commonProps = {
    $colorScheme: colorScheme,
    $size: size,
    $textSize: textSize,
    $variant: variant,
    $disableMotion: disableMotion,
    $iconColour: iconColour,
    $iconPosition: iconPosition,
    $showExternalLinkArrowIcon: showExternalLinkArrowIcon,
    className,
    disabled,
  };

  const baseMotionConfig = getMotionConfig(disabled, disableMotion);

  const baseProps = {
    'aria-label': ariaLabel,
    id,
    ...commonProps,
    ...baseMotionConfig,
  };

  // Determine component type and props based on link/button characteristics
  const linkType = getLinkType(href);
  const componentKey: ComponentMapKey = linkType || 'default';
  const ComponentType = componentMap[componentKey];
  let componentProps: Record<string, any>;

  if (linkType === 'hash' || linkType === 'external') {
    componentProps = {
      href: href || '',
      ...(linkType === 'external' && {
        target,
        rel: rel || (target === '_blank' ? 'noopener noreferrer' : undefined),
      }),
      onClick,
      ...baseProps,
    };
  } else if (linkType === 'internal') {
    componentProps = {
      to: href || '/',
      $isActive: (!!href && isActivePath(href)) || isActiveOverride,
      onClick: withSmoothScroll(href || '', onClick),
      ...baseProps,
    };
  } else {
    componentProps = {
      type,
      role: role || 'button',
      $isActive: isActiveOverride,
      onClick,
      ...baseProps,
    };
  }

  return React.createElement(
    ComponentType as React.ComponentType<any>,
    componentProps,
    renderContent()
  );
};

export default Button;
